import styled, { css } from 'styled-components/macro';

import { StyledDiv } from 'components/base/AnimatedLoadingWrapper';
import ContentLoader from 'components/utility/ContentLoader/ContentLoader';
import colors from 'styles/themes/colors';
import Box from 'components/elements/Box';

export const HeaderContainer = styled(StyledDiv)`
  display: flex;
  align-items: center;
  padding: calc(100px - ${({ theme }): string => theme.spacing.xlarge}) 0
    calc(55px - ${({ theme }): string => theme.spacing.xlarge}) 0;
  transition: ${({ theme }): string =>
    `opacity ${theme.transitionDuration} ${theme.timingFunction}`};
  opacity: ${({ loading }): number => (loading ? 0 : 1)};
`;

export const Header = styled('header')<{ simpleTextTopBar: boolean }>`
  display: flex;
  flex-direction: column;
  min-width: 100%;
  align-items: center;
  padding: ${({ theme, simpleTextTopBar }): string =>
      simpleTextTopBar ? theme.spacing.large : theme.spacing.xlarge}
    0;
  text-align: center;
  color: ${({ theme }): string => theme.White};

  @media screen and (min-width: 40em) {
    text-align: left;
    flex-flow: row nowrap;
    padding: ${({ theme }): string => theme.spacing.xxlarge} 0;
    justify-content: center;
  }
`;

export const ContentWrapper = styled('div')`
  @media screen and (min-width: 40em) {
    width: auto;
  }
`;

export const Title = styled('div')`
  font-size: 20px;
  line-height: 28px;
  padding: 0 55px;

  @media screen and (min-width: 40em) {
    font-size: 44px;
    line-height: 45px;
    padding: 0;
  }
`;

export const Container = styled('section')<any>`
  padding: ${({ theme, isMobileView }): string =>
    `${theme.spacing.large} ${
      isMobileView ? theme.spacing.large : theme.spacing.xxlarge
    }`};
  margin: 1em auto;
  background: white;
  background: white;

  @media screen and (min-width: 50em) {
    width: ${({ theme }): number => theme.layout.smallScreen * 100}%;
  }

  @media screen and (min-width: 80em) {
    width: ${({ theme }): number => theme.layout.largeScreen * 100}%;
  }
`;

export const H2 = styled('h2')`
  margin: 0 0 1em;
  font-size: 20px;
  font-weight: 700;

  @media screen and (min-width: 40em) {
    font-size: 1.5em;
  }
`;

export const StatusWrapper = styled('div')`
  margin-top: 0.3em;
  p {
    margin-bottom: 1.5em;
    font-size: 14px;
  }
`;

export const Loader = styled(ContentLoader)`
  width: 100%;
  max-width: 100%;
`;

export const LeftDataSection = styled.div`
  width: 30%;
  max-width: 250px;

  p {
    font-size: 14px;
  }
`;

export const RightDataSection = styled.div`
  width: 65%;
`;

export const HorizontalLine = styled.div`
  height: 1px;
  width: 100%;
  background-color: ${colors.LightGrey};
  margin: 1.4em 0;
`;

export const LinksWrapper = styled.div<{ isMobileView?: boolean }>`
  margin-top: ${({ isMobileView }): any => isMobileView && '1.5em'};
  a {
    box-shadow: none;
    color: ${colors.AccentGreen};
    font-weight: 600;
    line-height: 30px;
  }

  div {
    &:first-of-type {
      text-transform: capitalize;
    }
  }
`;

export const DataTable = styled.table`
  width: 100%;

  th {
    width: 50%;
    text-align: left;
    font-size: 11px;
    font-weight: 600;
    color: ${colors.MediumGrey};
    text-transform: uppercase;
    letter-spacing: 1px;
    padding-bottom: 0.5em;
  }

  @media screen and (max-width: 1080px) {
    th {
      &:first-of-type {
        width: 70%;
      }
      &:nth-child(2) {
        width: 30%;
      }
    }
  }
`;

export const TableRow = styled.tr`
  display: table;
  width: 100%;
  border-spacing: 0;
`;
export const TableHead = styled.thead<{
  withPadding?: boolean;
  isMobileView?: boolean;
}>`
  display: table;
  width: 100%;
  padding-right: ${({ theme, withPadding, isMobileView }): string =>
    withPadding && isMobileView ? theme.spacing.medium : 0};
`;
export const TableBody = styled.tbody<{ withPadding?: boolean }>`
  max-height: 392px;
  display: block;
  overflow-y: auto;
  padding-right: ${({ theme, withPadding }): string =>
    withPadding ? theme.spacing.medium : 0};

  ${({ withPadding }): any =>
    withPadding &&
    css`
      ${TableRow} {
        td {
          &:first-of-type {
            width: 52%;
          }
          &:nth-child(2) {
            width: 48%;
          }

          @media screen and (max-width: 1080px) {
            &:first-of-type {
              width: 72%;
            }
            &:nth-child(2) {
              width: 28%;
            }
          }
        }
      }
    `};

  /* Customize the scroll thumb */
  &::-webkit-scrollbar {
    width: 7px;
  }
  &::-webkit-scrollbar-thumb {
    width: 7px;
    border-radius: 6.5px;
    background-color: ${({ theme }): string => theme.LightGrey};
  }
  scrollbar-color: ${({ theme }): string =>
    `${theme.LightGrey} ${theme.White}`};
  scrollbar-width: thin;
`;

export const CellElement = styled.td`
  width: 50%;
  height: 49px;
  border-bottom: 1px solid ${colors.LightGrey};

  @media screen and (max-width: 1080px) {
    &:first-of-type {
      width: 70%;
    }
    &:nth-child(2) {
      width: 30%;
    }
  }
`;

export const DSPElement = styled.div`
  display: flex;
  align-items: center;

  svg {
    width: 32px;
    margin-right: ${({ theme }): string => theme.spacing.small};

    image {
      width: 32px;
    }
  }
`;

export const DateData = styled.div`
  max-width: 95px;
  display: flex;
  justify-content: center;
`;

export const SeeAllButton = styled.div`
  color: ${colors.AccentGreen};
  font-weight: 600;
  line-height: 30px;
  cursor: pointer;
  margin: ${({ theme }): string => theme.spacing.medium} 0;

  svg {
    margin-bottom: 1px;
  }
`;

export const ColumnWrapper = styled(Box)<{ isMobileView: boolean }>`
  display: flex;
  flex-direction: row;
  justify-content: space-between;

  ${({ isMobileView }): any =>
    isMobileView &&
    css`
      flex-direction: column;

      ${LeftDataSection} {
        width: 100%;
        max-width: 100%;
      }
      ${RightDataSection} {
        margin-top: 1em;
        width: 100%;

        thead {
          th {
            &:first-of-type {
              width: 65%;
              padding-left: 0;
            }
            &:nth-child(2) {
              text-align: right;
              padding-right: 0;
            }
          }
        }

        tbody {
          font-size: 14px;

          ${CellElement} {
            padding: 0;
            &:first-of-type {
              width: 65%;
            }
            &:nth-child(2) {
              div {
                float: right;
              }
            }
          }
        }
      }
    `}
`;
